import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from './Card';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  LineChart,
  TrendingUp,
  TrendingDown,
  Info,
  ChevronDown,
  Download,
} from 'lucide-react';
import { THROUGHPUT } from '../data';
import { TONES } from '../theme';
import DateRangePicker from './DateRangePicker';

const SERIES = [
  { key: 'certificates', label: 'Certificates', color: '#375AE7' },
  { key: 'verifications', label: 'Verifications', color: '#8B5CF6' },
];

/* ---------- Time-range options ---------- */
const RANGES = [
  { id: '4w', label: 'Last 4 weeks', weeks: 4, days: 28 },
  { id: '8w', label: 'Last 8 weeks', weeks: 8, days: 56 },
  { id: '12w', label: 'Last 12 weeks', weeks: 12, days: 84 },
];

const DAY_MS = 24 * 60 * 60 * 1000;
const baseWeeks = 12;

/* ---------- API wiring (GET /api/v1/agency/dashboard/throughput) ---------- */
const THROUGHPUT_ENDPOINT = '/api/v1/agency/dashboard/throughput';
const THROUGHPUT_EXPORT_ENDPOINT = `${THROUGHPUT_ENDPOINT}/export`;

/* weeks only accepts 4, 8 or 12; days only accepts 7, 30 or 90 */
const API_WEEKS = [4, 8, 12];
const API_DAYS = [7, 30, 90];

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/* Map the throughput payload onto the shape this card already renders:
   series → chart rows (`week` is the XAxis dataKey), totals → KPI tiles,
   insight → the insight line. */
const mapThroughput = (payload) => {
  const series = Array.isArray(payload?.series) ? payload.series : [];
  const totals = payload?.totals || {};
  const insight = payload?.insight || null;

  const rows = series.map((s) => ({
    week: s.label || '',
    startsOn: s.starts_on || '',
    certificates: num(s.certificates),
    verifications: num(s.verifications),
  }));

  return {
    weeks: num(payload?.weeks, baseWeeks),
    rangeLabel: payload?.range_label || null,
    rows,
    totals: {
      certificates:
        totals.certificates != null ? num(totals.certificates) : null,
      verifications:
        totals.verifications != null ? num(totals.verifications) : null,
      combined: totals.combined != null ? num(totals.combined) : null,
      dailyAverage:
        totals.daily_average != null ? num(totals.daily_average) : null,
      weeklyAverage:
        totals.weekly_average != null ? num(totals.weekly_average) : null,
      peakWeek: totals.peak_week != null ? num(totals.peak_week) : null,
    },
    insight: insight
      ? {
          leader: insight.leader || null,
          leaderTotal:
            insight.leader_total != null ? num(insight.leader_total) : null,
          growthPercent:
            insight.growth_percent != null ? num(insight.growth_percent) : null,
          fromValue:
            insight.from_value != null ? num(insight.from_value) : null,
          toValue: insight.to_value != null ? num(insight.to_value) : null,
          combinedVolume:
            insight.combined_volume != null
              ? num(insight.combined_volume)
              : null,
          title: insight.title || null,
          message: insight.message || null,
        }
      : null,
  };
};

const toInputDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const relativeInputDate = (offsetDays) => {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return toInputDate(date);
};

const formatDateLabel = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const getDateRangeDays = (start, end) => {
  if (!start || !end) return null;
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }

  const days = Math.round((endDate - startDate) / DAY_MS) + 1;
  return days > 0 ? days : null;
};

const fmt = (n) => n.toLocaleString('en-AU');
const pct = (n) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white/95 backdrop-blur px-3.5 py-2.5 shadow-lift text-xs">
      <p className="font-bold text-slate-700 mb-1.5">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 py-0.5">
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: p.color || p.stroke }}
          />
          <span className="text-slate-500">{p.name}</span>
          <span className="ml-auto font-bold text-slate-800 tabular-nums pl-5">
            {p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function ThroughputCard() {
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const navigate = useNavigate();
  const [hidden, setHidden] = useState({});
  const [range, setRange] = useState('12w'); // 4w | 8w | 12w | custom
  const [dateRange, setDateRange] = useState(() => ({
    start: relativeInputDate(-(baseWeeks * 7 - 1)),
    end: relativeInputDate(0),
  }));
  const [fold, setFold] = useState(false); // how-to-read expandable
  const [downloaded, setDownloaded] = useState(false);

  /* ---------- API state ---------- */
  const [apiThroughput, setApiThroughput] = useState(null);
  const [apiError, setApiError] = useState('');

  const toggle = (key) => setHidden((h) => ({ ...h, [key]: !h[key] }));

  const customDays = useMemo(
    () => getDateRangeDays(dateRange.start, dateRange.end),
    [dateRange],
  );
  const selectedPreset = RANGES.find((r) => r.id === range);
  const weeks =
    range === 'custom'
      ? Math.max(1, Math.ceil((customDays ?? baseWeeks * 7) / 7))
      : (selectedPreset?.weeks ?? baseWeeks);
  const dateError =
    !dateRange.start || !dateRange.end
      ? 'Select both a start and end date.'
      : !customDays
        ? 'End date must be on or after the start date.'
        : '';
  const rangeLabel =
    (range === 'custom'
      ? customDays
        ? `${formatDateLabel(dateRange.start)} – ${formatDateLabel(
            dateRange.end,
          )}`
        : 'Custom range'
      : selectedPreset?.label) ||
    apiThroughput?.range_label ||
    'Last 12 weeks';

  const handleRangeChange = ({ id, start, end }) => {
    setRange(id);
    setDateRange({ start, end });
  };

  /* ---------- Query string: weeks= for the week presets, days= for day
     presets, start_date/end_date for a custom range ---------- */
  const queryString = useMemo(() => {
    if (range === 'custom') {
      if (!customDays) return null; // invalid range → keep current view
      return `start_date=${dateRange.start}&end_date=${dateRange.end}`;
    }
    if (selectedPreset?.weeks && API_WEEKS.includes(selectedPreset.weeks)) {
      return `weeks=${selectedPreset.weeks}`;
    }
    const match = /(\d+)/.exec(range || '');
    const presetDays = match ? Number(match[1]) : null;
    if (presetDays && API_DAYS.includes(presetDays)) {
      return `days=${presetDays}`;
    }
    return 'weeks=12';
  }, [range, customDays, dateRange, selectedPreset]);
  const GetAPIcalls = async (url, navigate) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          if (navigate) navigate('/login');
          else window.location.assign('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };

  /* Same auth/redirect handling as GetAPIcalls, but returns the raw file
   (blob + filename) for endpoints that respond with CSV instead of JSON,
   e.g. /dashboard/throughput/export */
  const GetFileAPIcalls = async (url, navigate) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          if (navigate) navigate('/login');
          else window.location.assign('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = /filename="?([^";]+)"?/.exec(disposition);
      return { blob, filename: match ? match[1] : null };
    } catch (error) {
      console.error('API Call Error:', error);
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };

  useEffect(() => {
    if (!queryString) return undefined;
    let cancelled = false;

    const getThroughput = async () => {
      try {
        const response = await GetAPIcalls(
          `${THROUGHPUT_ENDPOINT}?${queryString}`,
          navigate,
        );
        if (cancelled) return;
        if (response.status == 200 && response.data) {
          setApiThroughput(response.data);
          setApiError('');
        } else {
          setApiThroughput(null);
        }
      } catch (err) {
        if (cancelled) return;
        setApiThroughput(null); // graceful fallback to the static snapshot
        setApiError(err.message);
        console.error(err.message);
      }
    };

    getThroughput();
    return () => {
      cancelled = true;
    };
  }, [queryString, navigate]);

  const mapped = useMemo(
    () => (apiThroughput ? mapThroughput(apiThroughput) : null),
    [apiThroughput],
  );

  /* Server already scopes the window, so its rows are used as-is;
     the static snapshot is only a fallback (scaled by the selected window). */
  const data = useMemo(() => {
    if (mapped) return mapped.rows;
    return THROUGHPUT.slice(-weeks);
  }, [mapped, weeks]);

  /* Derived summary per series + totals for the KPIs and insight line. */
  const summary = useMemo(() => {
    const out = {};
    SERIES.forEach((s) => {
      const vals = data
        .map((d) => d[s.key])
        .filter((n) => typeof n === 'number');
      const total = vals.reduce((a, b) => a + b, 0);
      const first = vals[0] ?? 0;
      const last = vals[vals.length - 1] ?? 0;
      const avg = vals.length ? total / vals.length : 0;
      const peak = vals.length ? Math.max(...vals) : 0;
      const apiTotal = mapped?.totals?.[s.key]; // totals.certificates | totals.verifications
      out[s.key] = {
        total: apiTotal != null ? apiTotal : total,
        avg,
        peak,
        change: first ? ((last - first) / first) * 100 : 0,
      };
    });
    const grandTotal =
      mapped?.totals?.combined ??
      SERIES.reduce((a, s) => a + (out[s.key]?.total ?? 0), 0);
    return { out, grandTotal };
  }, [data, mapped]);

  /* Leading series for the insight line (insight.leader when the API sends it). */
  const leader = useMemo(() => {
    const fromApi = SERIES.find((s) => s.key === mapped?.insight?.leader);
    if (fromApi) return fromApi;
    return SERIES.reduce((a, s) =>
      (summary.out[s.key]?.total ?? 0) >= (summary.out[a.key]?.total ?? 0)
        ? s
        : a,
    );
  }, [summary, mapped]);

  /* Insight values — API first, computed fallback. */
  const insightChange =
    mapped?.insight?.growthPercent ?? summary.out[leader.key]?.change ?? 0;
  const insightFrom = mapped?.insight?.fromValue ?? data[0]?.[leader.key] ?? 0;
  const insightTo =
    mapped?.insight?.toValue ?? data[data.length - 1]?.[leader.key] ?? 0;
  const insightCombined = mapped?.insight?.combinedVolume ?? summary.grandTotal;
  const insightTitle =
    mapped?.insight?.title ||
    `${leader.label} leads at ${fmt(summary.out[leader.key]?.total ?? 0)}`;

  /* KPI tiles: totals.daily_average / totals.peak_week when available. */
  const dailyAverage =
    mapped?.totals?.dailyAverage ?? summary.out[leader.key]?.avg ?? 0;
  const peakWeek =
    mapped?.totals?.peakWeek ??
    Math.max(...SERIES.map((s) => summary.out[s.key]?.peak ?? 0));

  const anyHidden = SERIES.some((s) => hidden[s.key]);

  const handleDownload = async () => {
    const fileRange =
      range === 'custom'
        ? `${dateRange.start || 'start'}-${dateRange.end || 'end'}`
        : range;
    const fallbackName = `throughput-${fileRange}.csv`;

    const saveBlob = (blob, filename) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 1600);
    };

    /* Server CSV (/throughput/export) using the same query as the chart. */
    if (queryString) {
      try {
        const { blob, filename } = await GetFileAPIcalls(
          `${THROUGHPUT_EXPORT_ENDPOINT}?${queryString}`,
          navigate,
        );
        saveBlob(blob, filename || fallbackName);
        return;
      } catch (err) {
        console.error(err.message); // fall through to the local CSV
      }
    }

    /* Fallback: build the CSV from the data currently on screen. */
    const rows = [
      ['week', ...SERIES.map((s) => s.label)],
      ...data.map((d) => [d.week, ...SERIES.map((s) => d[s.key])]),
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    saveBlob(new Blob([csv], { type: 'text/csv' }), fallbackName);
  };

  return (
    <Card delay={180} className="p-5 md:p-6 h-full">
      {/* ---------- Header ---------- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span
            className={`w-9 h-9 rounded-xl ${TONES.blue.tile} flex items-center justify-center transition-transform duration-300 hover:scale-110`}
          >
            <LineChart size={17} strokeWidth={1.9} />
          </span>
          <div>
            <h2 className="font-semibold text-slate-900">Throughput</h2>
            <p className="text-[11px] text-slate-400">
              Certificates vs verifications · {rangeLabel}
            </p>
          </div>
        </div>

        {/* Reusable date range picker + export */}
        <div
          className="flex w-full items-center justify-end gap-2 flex-wrap sm:w-auto"
          title={apiError || undefined}
        >
          <DateRangePicker
            range={range}
            startDate={dateRange.start}
            endDate={dateRange.end}
            presets={RANGES}
            onChange={handleRangeChange}
          />
          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-slate-300 bg-white text-[11px] font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition-all active:scale-95"
          >
            {downloaded ? (
              <span className="text-emerald-500 font-bold">✓</span>
            ) : (
              <Download size={13} />
            )}
            CSV
          </button>
          {dateError && (
            <p className="basis-full text-left text-[11px] font-medium text-rose-600 sm:text-right">
              {dateError}
            </p>
          )}
        </div>

        {/* Interactive legend */}
        <div className="flex items-center gap-2 w-full md:w-auto">
          {SERIES.map((s) => (
            <button
              key={s.key}
              onClick={() => toggle(s.key)}
              className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 ring-1 transition-all duration-200 active:scale-95 ${
                hidden[s.key]
                  ? 'text-slate-400 bg-slate-50 ring-slate-200 line-through'
                  : 'text-slate-700 bg-white ring-slate-200 hover:ring-slate-300 shadow-sm'
              }`}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{
                  background: s.color,
                  opacity: hidden[s.key] ? 0.3 : 1,
                }}
              />
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ---------- Summary KPIs ---------- */}
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        {SERIES.map((s) => {
          const up = (summary.out[s.key]?.change ?? 0) >= 0;
          return (
            <div
              key={s.key}
              className={`rounded-xl border border-slate-200/70 bg-slate-50/40 px-3 py-2.5 transition-opacity ${
                hidden[s.key] ? 'opacity-40' : ''
              }`}
            >
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: s.color }}
                />
                {s.label}
              </div>
              <div className="mt-1 text-lg font-bold tracking-tight text-slate-900 tabular-nums">
                {fmt(summary.out[s.key]?.total ?? 0)}
              </div>
            </div>
          );
        })}
        <div className="rounded-xl border border-slate-200/70 bg-slate-50/40 px-3 py-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Daily avg
          </div>
          <div className="mt-1 text-lg font-bold tracking-tight text-slate-900 tabular-nums">
            {fmt(Math.round(dailyAverage))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200/70 bg-slate-50/40 px-3 py-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Peak
          </div>
          <div className="mt-1 text-lg font-bold tracking-tight text-slate-900 tabular-nums">
            {fmt(peakWeek)}
          </div>
        </div>
      </div>

      {/* ---------- Chart ---------- */}
      <div className="h-64 mt-5">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
          >
            <defs>
              <linearGradient id="gCert" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#375AE7" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#375AE7" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gVer" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.22} />
                <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="#EEF2F7" />
            <XAxis
              dataKey="week"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: '#94A3B8' }}
              dy={6}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: '#94A3B8' }}
            />
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ stroke: '#CBD5E1', strokeDasharray: '4 4' }}
            />
            {SERIES.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2.5}
                fill={`url(#${s.key === 'certificates' ? 'gCert' : 'gVer'})`}
                hide={!!hidden[s.key]}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
                animationDuration={1200}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* ---------- Insight ---------- */}
      {!anyHidden && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3.5">
          <span className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
            {insightChange >= 0 ? (
              <TrendingUp size={15} />
            ) : (
              <TrendingDown size={15} />
            )}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800">
              {insightTitle}
            </p>
            <p className="text-sm text-slate-600 mt-0.5 leading-snug">
              Over {rangeLabel}, {leader.label.toLowerCase()} changed by{' '}
              <b className="text-emerald-600">{pct(insightChange)}</b> from{' '}
              {fmt(insightFrom)} to {fmt(insightTo)} per week. Combined volume:{' '}
              <b>{fmt(insightCombined)}</b>.
            </p>
          </div>
        </div>
      )}

      {/* ---------- How to read ---------- */}
      <div className="mt-3 rounded-xl border border-slate-200/70 bg-slate-50/50 overflow-hidden">
        <button
          onClick={() => setFold((f) => !f)}
          className="w-full flex items-center gap-2 px-3.5 py-3 text-sm font-semibold text-slate-700 hover:text-brand transition-colors"
        >
          <Info size={15} className="text-brand" />
          How to read this chart
          <ChevronDown
            size={16}
            className={`ml-auto text-slate-400 transition-transform duration-200 ${fold ? 'rotate-180' : ''}`}
          />
        </button>
        {fold && (
          <div className="px-3.5 pb-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-600">
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-[#375AE7] shrink-0" />
              The blue line is certificates; the violet line is verifications.
            </div>
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-[#8B5CF6] shrink-0" />
              The filled area under each line shows its volume over time.
            </div>
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-slate-400 shrink-0" />
              Click a legend chip to show or hide that series.
            </div>
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-slate-400 shrink-0" />
              Hover the chart for exact figures on any given week.
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
